/**
 * Domain VNNIC Information Column Configuration
 */
import type { DataTableColumn } from '@/components/ui/data-table-customized'
import {
    createCurrencyFormatter,
    createDateFormatter,
    createTimeRangeFormatter,
    createOptionsFormatter,
    OptionItem,
} from '@/components/ui/data-table-customized'
import type { TFunction } from 'i18next'
import { Service, ServiceStatus } from '@/modules/Service/types'
import { SERVICE_STATUS } from '@/modules/Service/constants'
import ServiceContract from '../../../shared/ServiceContract'

// Row type for Domain table
export type DomainRow = Service & {
    domain_name?: string
}

// ============================================================================
// Status Options
// ============================================================================

const getStatusOptions = (t: TFunction): OptionItem[] =>
    SERVICE_STATUS(t).map((item) => ({
        label: item.label,
        value: item.value,
        color: item.color as OptionItem['color'],
    }))

// ============================================================================
// Column Configuration
// ============================================================================

export const createDomainColumns = (
    t: TFunction
): DataTableColumn<DomainRow>[] => [
    {
        key: 'domain_name',
        title: t('domain_name'),
        width: 200,
        pinned: 'left',
        render: (value, row) =>
            (value || row?.name || '-')?.toString()?.toUpperCase(),
    },
    {
        key: 'employee_names',
        title: t('employee_sale_tracking_ids'),
        width: 200,
        render: (value) => value?.join(', ') || '-',
    },
    {
        key: 'service_price_register',
        title: t('service_price_register'),
        width: 120,
        cellClassName: 'text-right',
        formatter: createCurrencyFormatter({ currency: '' }),
    },
    {
        key: 'service_price_recurring',
        title: t('service_price_recurring'),
        width: 130,
        cellClassName: 'text-right',
        render: (value) => {
            if (!value) return '-'
            return undefined
        },
        formatter: createCurrencyFormatter({ currency: '' }),
    },
    {
        key: 'service_status',
        title: t('service_status'),
        width: 150,
        formatter: createOptionsFormatter({
            options: getStatusOptions(t),
        }),
    },
    {
        key: 'service_start_date',
        title: t('service_start_date'),
        width: 110,
        cellClassName: 'text-right',
        formatter: createDateFormatter({ format: 'DD/MM/YYYY' }),
    },
    {
        key: 'service_stopped_date',
        title: t('service_stopped_date'),
        width: 110,
        cellClassName: 'text-right',
        hidden: true, // Will be shown conditionally
        formatter: createDateFormatter({ format: 'DD/MM/YYYY' }),
    },
    {
        key: 'service_active_cycling',
        title: t('service_active_cycling'),
        width: 180,
        cellClassName: 'text-right',
        formatter: createTimeRangeFormatter({
            startKey: 'current_startdate',
            endKey: 'current_enddate',
            format: 'DD/MM/YYYY',
            smartYear: true,
        }),
    },
    {
        key: 'service_renew_cycling',
        title: t('service_renew_cycling'),
        width: 180,
        cellClassName: 'text-right',
        hidden: true, // Will be shown conditionally
        render: (_, row) => {
            if (row?.service_status !== ServiceStatus.Active) return null
            return undefined
        },
        formatter: createTimeRangeFormatter({
            startKey: 'continue_startdate',
            endKey: 'continue_enddate',
            format: 'DD/MM/YYYY',
            smartYear: true,
        }),
    },
    {
        key: 'service_contract',
        title: t('service_contract'),
        width: 300,
        render: (_, row) => {
            if (!row?.service_id) return null
            return <ServiceContract serviceID={row.service_id} />
        },
    },
]
